import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomLoader } from "@admin/components/ui/custom-loader";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TableCell, TableHead } from "../ui/table";
import { DataTable, type TableColumnDef } from "../data-table";
import {
  Download,
  Filter,
  RotateCcw,
  ArrowDownUp,
  LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import type {
  ReportConfig,
  ReportExportableRow,
  ReportFilterDef,
} from "./types";
import { CommonPagination } from "../CommonPagination";
import { exportReportToCsv } from "../../lib/exportReport";

interface DynamicReportProps<T> {
  config: ReportConfig<T>;
  icon: LucideIcon;
  hydrateFilters?: (
    rows: T[]
  ) => Promise<ReportFilterDef[]> | ReportFilterDef[];
  /** Used when config.serverFetch is set (e.g. institution-approvals API) */
  defaultPageSize?: number;
}

function buildInitialFilters(
  config: ReportConfig<unknown>
): Record<string, string> {
  const values: Record<string, string> = { ...config.defaultFilterValues };
  for (const filter of config.filters) {
    values[filter.key] ??=
      filter.defaultValue ?? (filter.type === "search" ? "" : "all");
  }
  return values;
}

export default function DynamicReport<T extends ReportExportableRow>({
  config,
  icon: Icon,
  hydrateFilters,
  defaultPageSize = 10,
}: DynamicReportProps<T>) {
  const isServerMode = Boolean(config.serverFetch);
  const [allRows, setAllRows] = useState<T[]>([]);
  const [serverTotalCount, setServerTotalCount] = useState(0);
  const [filterDefs, setFilterDefs] = useState<ReportFilterDef[]>(
    config.filters
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    buildInitialFilters(config as ReportConfig<unknown>)
  );
  const [debouncedFilters, setDebouncedFilters] = useState(filterValues);
  const [sortBy, setSortBy] = useState(config.defaultSort);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedMap, setSelectedMap] = useState<Map<string | number, T>>(
    () => new Map()
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filterValues), 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  const activeFilters = isServerMode ? debouncedFilters : filterValues;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (config.serverFetch) {
        const result = await config.serverFetch({
          page: currentPage,
          page_size: pageSize,
          filters: activeFilters,
          sortKey: sortBy,
        });
        setAllRows(result.rows);
        setServerTotalCount(result.totalCount);
        if (hydrateFilters && currentPage === 1) {
          const next = await hydrateFilters(result.rows);
          setFilterDefs(next);
        }
      } else if (config.fetchData) {
        const rows = await config.fetchData();
        setAllRows(rows);
        setServerTotalCount(rows.length);
        if (hydrateFilters) {
          const next = await hydrateFilters(rows);
          setFilterDefs(next);
        }
      }
    } catch {
      toast.error(`Failed to load ${config.title.toLowerCase()}`);
      setAllRows([]);
      setServerTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [config, hydrateFilters, currentPage, pageSize, activeFilters, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    if (isServerMode) {
      return allRows;
    }
    return config.applyFilters(allRows, filterValues);
  }, [allRows, config, filterValues, isServerMode]);

  const sortedRows = useMemo(() => {
    if (isServerMode) {
      return filteredRows;
    }
    return config.sortRows(filteredRows, sortBy);
  }, [config, filteredRows, sortBy, isServerMode]);

  const resultCount = isServerMode ? serverTotalCount : sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(resultCount / pageSize));
  const paginatedRows = useMemo(() => {
    if (isServerMode) {
      return sortedRows;
    }
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize, isServerMode]);

  const displayRows = paginatedRows;
  const selectedRows = useMemo(
    () => Array.from(selectedMap.values()),
    [selectedMap]
  );
  const selectedCount = selectedRows.length;

  const allPageSelected =
    displayRows.length > 0 && displayRows.every(row => selectedMap.has(row.id));
  const somePageSelected =
    displayRows.some(row => selectedMap.has(row.id)) && !allPageSelected;

  const toggleRowSelection = (row: T, checked: boolean) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (checked) {
        next.set(row.id, row);
      } else {
        next.delete(row.id);
      }
      return next;
    });
  };

  const toggleAllOnPage = (checked: boolean) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      displayRows.forEach(row => {
        if (checked) {
          next.set(row.id, row);
        } else {
          next.delete(row.id);
        }
      });
      return next;
    });
  };

  const clearSelection = () => setSelectedMap(new Map());

  useEffect(() => {
    setCurrentPage(1);
    setSelectedMap(new Map());
  }, [activeFilters, sortBy, pageSize]);

  const updateFilter = (key: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilterValues(buildInitialFilters(config as ReportConfig<unknown>));
    setSortBy(config.defaultSort);
    setCurrentPage(1);
    setSelectedMap(new Map());
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      if (selectedCount > 0) {
        exportReportToCsv(selectedRows, config.exportFileName);
        toast.success(`Exported ${selectedCount} selected row(s) to CSV`);
        return;
      }

      let rows: T[] = isServerMode ? [] : sortedRows;
      if (config.serverFetch) {
        const exportSize = Math.max(serverTotalCount, 5000);
        const result = await config.serverFetch({
          page: 1,
          page_size: exportSize,
          filters: activeFilters,
          sortKey: sortBy,
        });
        rows = result.rows;
        if (result.totalCount > exportSize) {
          toast.info(
            `Exported first ${exportSize} of ${result.totalCount} records`
          );
        }
      }
      if (rows.length === 0) {
        toast.error("No data to export");
        return;
      }
      exportReportToCsv(rows, config.exportFileName);
      toast.success(`Exported ${rows.length} rows to CSV`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // const handleExportExcel = async () => {
  //   await handleExportCsv();
  //   toast.info("Excel export uses CSV format compatible with Excel");
  // };

  const reportTableColumns = useMemo<TableColumnDef<T>[]>(
    () =>
      config.columns.map(col => ({
        id: col.key,
        label: col.label,
        headerClassName:
          "text-xs font-semibold uppercase tracking-wide text-muted-foreground",
        cellClassName: col.className,
        width: 160,
        renderCell: (row: T) =>
          col.render ? col.render(row) : String(col.accessor(row) ?? "—"),
      })),
    [config.columns]
  );

  const renderFilter = (filter: ReportFilterDef) => {
    if (filter.type === "search") {
      return (
        <div key={filter.key} className="space-y-2">
          <Label>{filter.label}</Label>
          <Input
            placeholder={filter.placeholder}
            value={filterValues[filter.key] ?? ""}
            onChange={e => updateFilter(filter.key, e.target.value)}
          />
        </div>
      );
    }

    const options = filter.options ?? [];
    return (
      <div key={filter.key} className="space-y-2">
        <Label>{filter.label}</Label>
        <Select
          value={filterValues[filter.key] ?? "all"}
          onValueChange={v => updateFilter(filter.key, v)}
        >
          <SelectTrigger>
            <SelectValue placeholder={`All ${filter.label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <div className="min-w-0 space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Icon className="h-6 w-6 text-primary" />
          </span>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {config.title}
            </h1>
            <p className="text-muted-foreground">{config.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedCount > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {selectedCount} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                disabled={exporting || loading}
              >
                Clear selection
              </Button>
            </>
          )}
          <Button
            variant="default"
            onClick={handleExportCsv}
            disabled={exporting || loading}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting
              ? "Exporting..."
              : selectedCount > 0
                ? `Export (${selectedCount})`
                : "Export CSV"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Records</p>
            <p className="text-2xl font-bold text-foreground">{resultCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              {isServerMode ? "On This Page" : "Filtered Results"}
            </p>
            <p className="text-2xl font-bold text-primary">
              {displayRows.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Selected</p>
            <p className="text-2xl font-bold text-foreground">
              {selectedCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Page</p>
            <p className="text-2xl font-bold text-foreground">
              {resultCount === 0 ? 0 : currentPage} / {totalPages}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5 text-primary" />
            Filters
          </CardTitle>
          <Button variant="outline" size="sm" onClick={resetFilters}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {filterDefs.map(renderFilter)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <CustomLoader size="md" />
            </div>
          ) : (
            <>
              <DataTable
                tableId={`report-${config.id}`}
                title="Report Data"
                columns={reportTableColumns}
                data={displayRows}
                getRowKey={row => row.id}
                emptyMessage="No records match your filters"
                headerRowClassName="bg-muted/40 hover:bg-muted/40"
                leadingColumnCount={2}
                toolbar={
                  <div className="flex items-center gap-2">
                    <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
                    <Label className="shrink-0 text-sm">Sort by</Label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-8 w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {config.sortOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                }
                renderLeadingHead={() => (
                  <>
                    <TableHead className="w-12 pl-4">
                      <Checkbox
                        checked={
                          allPageSelected
                            ? true
                            : somePageSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={v => toggleAllOnPage(v === true)}
                        aria-label="Select all on this page"
                      />
                    </TableHead>
                    <TableHead className="w-12 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      #
                    </TableHead>
                  </>
                )}
                renderLeadingCell={(row, index) => (
                  <>
                    <TableCell className="pl-4">
                      <Checkbox
                        checked={selectedMap.has(row.id)}
                        onCheckedChange={v =>
                          toggleRowSelection(row, v === true)
                        }
                        aria-label="Select row"
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                  </>
                )}
                rowClassName={() => "hover:bg-muted/30"}
              />
              {resultCount > 0 && (
                <div className="border-t px-2 py-4">
                  <CommonPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalCount={resultCount}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={size => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
